
import { defineComponent, inject } from 'vue'
import CloseSvg from '@/components/svgs/CloseSvg.vue'
import DragAndDropSvg from '../svgs/DragAndDropSvg.vue'
import draggable from 'vuedraggable'
import Dropdown from '@/components/shared/Dropdown.vue'
import TextInput from '@/components/shared/TextInput.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import LockingButton from '@/components/shared/LockingButton.vue'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import { ServiceField } from '@/@types/responses/service/ServiceField'
import { ServiceFieldOption } from '@/@types/responses/service/ServiceFieldOption'
import CurrencyInput from '@/components/shared/CurrencyInput.vue'
import { ServiceService } from '@/services/service.service'
import { ServiceFieldOptionMessage } from '@/@types/messages/ServiceFieldOptionMessage'
import { ServiceFieldService } from '@/services/serviceField.service'
import { CreateServiceFieldMessage } from '@/@types/messages/CreateServiceFieldMessage'
import { UpdateServiceFieldMessage } from '@/@types/messages/UpdateServiceFieldMessage'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'ServiceFieldModal',
  components: {
    CloseSvg,
    CurrencyInput,
    DragAndDropSvg,
    draggable,
    Dropdown,
    LockingButton,
    TextInput,
    TrashCanSvg
  },
  props: {
    serviceField: {
      type: ServiceField,
      required: true
    },
    serviceId: {
      type: Number,
      required: true
    }
  },
  emits: ['service-field-changed', 'close'],
  setup() {
    const serviceFieldService = inject(PROVIDER_CONSTANTS.serviceFieldService) as ServiceFieldService
    const serviceService = inject(PROVIDER_CONSTANTS.serviceService) as ServiceService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      serviceFieldService,
      serviceService,
      toast
    }
  },
  data() {
    const typeOptions = [
      new DropdownOptionResponse(1, true, 'Cartridge'),
      new DropdownOptionResponse(2, true, 'Selection')
    ]

    return {
      name: this.serviceField.name,
      saving: false,
      serviceFieldOptions: JSON.parse(JSON.stringify(this.serviceField.serviceFieldOptions)) as ServiceFieldOption[],
      type: this.serviceField.type,
      typeOptions
    }
  },
  computed: {
    isFormValid(): boolean {
      let optionsChanged = false

      if (this.serviceFieldOptions.length !== this.serviceField.serviceFieldOptions.length)
        optionsChanged = true
      else {
        for (let i = 0; i < this.serviceFieldOptions.length; i++) {
          if ((this.serviceFieldOptions[i].active !== this.serviceField.serviceFieldOptions[i].active) ||
              (this.serviceFieldOptions[i].additionalCost !== this.serviceField.serviceFieldOptions[i].additionalCost) ||
              (this.serviceFieldOptions[i].displayOrder !== this.serviceField.serviceFieldOptions[i].displayOrder) ||
              (this.serviceFieldOptions[i].value !== this.serviceField.serviceFieldOptions[i].value)) {
            optionsChanged = true
            break
          }
        }
      }

      return (this.name !== this.serviceField.name ||
             this.type !== this.serviceField.type ||
             optionsChanged) && (this.name.trim() !== '' && this.type > 0 && this.type <= 2 && !this.serviceFieldOptions.some(s => s.value.trim() === '' || s.additionalCost < 0 || s.displayOrder < 0))
    },
    titleText(): string {
      return this.serviceField.id === 0 ? 'Create Field' : 'Edit Field'
    },
    typeText(): string {
      if (!this.serviceField) return ''

      const index = this.typeOptions.findIndex(t => t.id === this.serviceField.type)
      if (index === -1) return ''

      return this.typeOptions[index].name
    }
  },
  mounted() {
    document.body.addEventListener('keyup', this.onKeyup)
  },
  unmounted() {
    document.body.removeEventListener('keyup', this.onKeyup)
  },
  methods: {
    onAddOptionClicked(): void {
      let displayOrder = 1

      if (this.serviceFieldOptions.length > 0) {
        const currentMaxValue = this.serviceFieldOptions[this.serviceFieldOptions.length - 1].displayOrder
        displayOrder = currentMaxValue + 1
      }

      this.serviceFieldOptions.push(new ServiceFieldOption(0, true, 0, displayOrder, ''))
    },
    onCloseClicked(): void {
      this.$emit('close')
    },
    onDeleteClicked(serviceFieldOption: ServiceFieldOption): void {
      serviceFieldOption.active = false
    },
    onKeyup(e: KeyboardEvent): void {
      if (e.key === 'Escape' || e.key === 'Esc')
        this.onCloseClicked()
    },
    onSaveClicked(): void {
      this.saving = true

      const serviceFieldOptionMessages = this.serviceFieldOptions.map((serviceFieldOption: ServiceFieldOption) => new ServiceFieldOptionMessage(serviceFieldOption.id, serviceFieldOption.active, serviceFieldOption.additionalCost, serviceFieldOption.displayOrder, String(serviceFieldOption.value)))
      if (this.serviceField.id === 0) {
        const createServiceFieldMessage = new CreateServiceFieldMessage(this.name, this.type, serviceFieldOptionMessages)
        this.serviceService.createServiceField(this.serviceId, createServiceFieldMessage).then(objectCreatedResponse => {
          this.$emit('service-field-changed', objectCreatedResponse.id)
          this.toast.success('Field created successfully.')
          this.$emit('close')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the field. Please try again.')
          }
        })
      } else {
        const updateServiceFieldMessage = new UpdateServiceFieldMessage(this.name, serviceFieldOptionMessages)
        this.serviceFieldService.updateServiceField(this.serviceField.id, updateServiceFieldMessage).then(field => {
          this.$emit('service-field-changed', field)
          this.toast.success('Field updated successfully.')
          this.$emit('close')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the field. Please try again.')
          }
        })
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onServiceFieldOptionDisplayOrderChanged(event: any): void {
      const oldIndex = event.moved.oldIndex

      let newIndex = event.moved.newIndex
      if (newIndex === this.serviceFieldOptions.length)
        newIndex -= 1;
      
      const offset = newIndex - oldIndex > 0 ? -1 : 1
      const positionsToMove = Math.abs(newIndex - oldIndex)
      for (let i = 0; i < positionsToMove; i++) {
        const swapOrder = this.serviceFieldOptions[newIndex].displayOrder;
        this.serviceFieldOptions[newIndex].displayOrder = this.serviceFieldOptions[newIndex + offset].displayOrder;
        this.serviceFieldOptions[newIndex + offset].displayOrder = swapOrder;
        newIndex += offset
      }
    }
  }
})
